import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { 
  Trophy, 
  Target, 
  TrendingUp, 
  TrendingDown, 
  Minus,
  Calendar,
  Shield,
  Award,
  Loader2,
  Flame,
  Crown,
  Star,
  Users,
  Zap,
  Medal,
  Share2,
  Download,
  X,
  Sparkles
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CoachesOffice() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [shareSummary, setShareSummary] = useState({ week: 0, total: 0, daily: [0, 0, 0, 0, 0, 0, 0] });
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [referredCount, setReferredCount] = useState(0);
  // Dismissible share nudge — remembered across sessions in localStorage.
  const [shareNudgeDismissed, setShareNudgeDismissed] = useState(() => {
    try {
      return typeof window !== 'undefined' && window.localStorage.getItem('coach-office-share-nudge-dismissed') === 'true';
    } catch {
      return false;
    }
  });
  const dismissShareNudge = () => {
    setShareNudgeDismissed(true);
    try { window.localStorage.setItem('coach-office-share-nudge-dismissed', 'true'); } catch {
      /* localStorage disabled — nudge just stays hidden for this session */
    }
  };

  useEffect(() => {
    fetchPerformanceData();
    fetchShareSummary();
    // Referral count — how many coaches have signed up via this coach's
    // share link. Silent-fails: the nudge just doesn't show the badge.
    // Also handles the milestone celebration loop — the backend returns
    // any newly-crossed milestone thresholds (1, 5, 10, 25, 50, 100) in
    // `newly_hit_milestones`, and each one gets ONE dedicated celebration
    // toast (server marks them celebrated atomically so this only fires
    // once per milestone, ever).
    (async () => {
      try {
        const { data: refData } = await axios.get(`${API}/coach/referral-stats`);
        setReferredCount(refData?.referred_count || 0);
        const hits = Array.isArray(refData?.newly_hit_milestones) ? refData.newly_hit_milestones : [];
        hits.forEach((m, i) => {
          const copy = m === 1
            ? { title: 'Your first referred coach!', body: 'Someone just signed up via your share link. Nice work — every share compounds.' }
            : { title: `${m} coaches inspired — huge milestone!`, body: `You've now brought ${m} coaches to Grassroots Coaching. That's real impact — keep sharing.` };
          setTimeout(() => {
            toast.success(copy.title, {
              description: copy.body,
              duration: 8000,
              icon: '🎉',
            });
          }, i * 900);
        });
      } catch { /* silent — nudge falls back to the default copy */ }
    })();
  }, []);

  const handleDownloadCV = async () => {
    setDownloading(true);
    try {
      const response = await axios.get(`${API}/coach/performance/pdf`, {
        responseType: 'blob',
      });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const safe = (user?.name || 'Coach').replace(/[^A-Za-z0-9]+/g, '_').replace(/^_|_$/g, '');
      a.download = `${safe}_Coach_CV.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success('Coach CV downloaded');
    } catch (error) {
      console.error('Failed to download Coach CV:', error);
      toast.error('Failed to download CV — please try again');
    } finally {
      setDownloading(false);
    }
  };

  const handleShare = async () => {
    if (!data) return;
    const { coach_name, team_name, total_stats } = data;

    // Ensure the coach has a public share link — generate on first use.
    // Idempotent server-side (reuses existing slug).
    let socialUrl = null;
    let publicUrl = null;
    try {
      const resp = await axios.post(`${API}/coach/enable-public-profile`);
      const slug = resp.data?.public_slug;
      if (slug) {
        // Public SPA view (pretty URL for direct viewing)
        publicUrl = `${window.location.origin}/coach/${slug}`;
        // Social-scraper URL — serves Open Graph tags so previews on
        // WhatsApp / X / iMessage / LinkedIn / Slack show the coach's name,
        // team and stats instead of the generic site card. Redirects to
        // publicUrl for real users on click (~0.5s).
        socialUrl = `${window.location.origin}/api/coach/public/${slug}/share`;
      }
    } catch {
      // Non-blocking — share still works with just the text summary.
    }

    const shareUrl = socialUrl || publicUrl;
    const lines = [
      `${coach_name} — Coach's Office`,
      team_name,
      '',
      `${total_stats.total_seasons} season${total_stats.total_seasons === 1 ? '' : 's'} · ${total_stats.matches_played} matches · ${total_stats.win_percentage}% win rate`,
      `${total_stats.wins}W ${total_stats.draws}D ${total_stats.losses}L · GF ${total_stats.goals_scored} · GA ${total_stats.goals_conceded}`,
    ];
    if (shareUrl) {
      lines.push('', `View my full profile: ${shareUrl}`);
    } else {
      lines.push('', 'Tracked with Grassroots Coaching');
    }
    const summary = lines.join('\n');

    // Prefer native share sheet (mobile / modern browsers)
    if (typeof navigator !== 'undefined' && navigator.share) {
      try {
        await navigator.share({
          title: `${coach_name} — Coach's Office`,
          text: summary,
          url: shareUrl || undefined,
        });
        return;
      } catch (err) {
        if (err?.name === 'AbortError') return;
      }
    }
    // Fallback: copy to clipboard
    try {
      await navigator.clipboard.writeText(summary);
      toast.success(shareUrl
        ? 'Profile link + summary copied to clipboard'
        : 'Coach summary copied to clipboard');
    } catch {
      toast.error('Could not copy — long-press to select manually');
    }
  };

  const fetchPerformanceData = async () => {
    try {
      const response = await axios.get(`${API}/coach/performance`);
      setData(response.data);
    } catch (error) {
      console.error('Failed to fetch performance data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchShareSummary = async () => {
    try {
      const response = await axios.get(`${API}/players/share-events/summary`);
      setShareSummary({
        week: response.data?.week || 0,
        total: response.data?.total || 0,
        daily: Array.isArray(response.data?.daily) && response.data.daily.length === 7
          ? response.data.daily
          : [0, 0, 0, 0, 0, 0, 0],
      });
    } catch (_) {
      // Silent — tile will show 0 if endpoint fails
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString + 'T00:00:00');
    return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const Sparkline = ({ values }) => {
    const w = 110;
    const h = 36;
    const pad = 2;
    const max = Math.max(1, ...values);
    const stepX = (w - pad * 2) / (values.length - 1);
    const points = values.map((v, i) => {
      const x = pad + i * stepX;
      const y = h - pad - (v / max) * (h - pad * 2);
      return [x, y];
    });
    const path = points
      .map((p, i) => `${i === 0 ? 'M' : 'L'}${p[0].toFixed(1)},${p[1].toFixed(1)}`)
      .join(' ');
    const area = `${path} L${points[points.length - 1][0].toFixed(1)},${h - pad} L${pad},${h - pad} Z`;
    const last = points[points.length - 1];
    return (
      <svg
        width={w}
        height={h}
        viewBox={`0 0 ${w} ${h}`}
        className="shrink-0"
        data-testid="shares-sparkline"
        role="img"
        aria-label="Daily shares over the last 7 days"
      >
        <path d={area} fill="rgba(249, 115, 22, 0.18)" />
        <path d={path} fill="none" stroke="#f97316" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
        <circle cx={last[0]} cy={last[1]} r="2.5" fill="#f97316" />
      </svg>
    );
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
        </div>
      </Layout>
    );
  }

  if (!data || data.season_stats?.length === 0) {
    return (
      <Layout>
        <div className="text-center py-16">
          <div className="w-16 h-16 rounded-2xl bg-gray-100 flex items-center justify-center mx-auto mb-4">
            <Trophy className="w-8 h-8 text-gray-400" strokeWidth={1.5} />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">No Performance Data</h2>
          <p className="text-gray-500">Complete some matches to see your coaching record.</p>
        </div>
      </Layout>
    );
  }

  const { coach_name, team_name, total_stats, season_stats } = data;

  return (
    <Layout>
      <div className="space-y-6 page-slide-in" data-testid="coaches-office-container">
        {/* Header */}
        <div className="flex items-start sm:items-center justify-between gap-3 flex-wrap">
          <div>
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 font-title tracking-tight">
              Coach&apos;s Office
            </h1>
            <p className="text-gray-500 mt-1">Your complete coaching record and performance history</p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button
              type="button"
              onClick={handleShare}
              className="inline-flex items-center gap-1.5 rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 hover:border-gray-300 transition-colors"
              data-testid="coach-office-share-btn"
              title="Share this profile"
            >
              <Share2 className="w-4 h-4" strokeWidth={2} />
              <span className="hidden sm:inline">Share</span>
            </button>
            <button
              type="button"
              onClick={handleDownloadCV}
              disabled={downloading}
              className="inline-flex items-center gap-1.5 rounded-xl px-3 py-2 text-sm font-semibold text-white transition-colors disabled:opacity-60"
              style={{ backgroundColor: user?.button_color || '#064E3B' }}
              data-testid="coach-office-download-cv-btn"
              title="Download as PDF"
            >
              {downloading ? (
                <Loader2 className="w-4 h-4 animate-spin" strokeWidth={2} />
              ) : (
                <Download className="w-4 h-4" strokeWidth={2} />
              )}
              <span className="hidden sm:inline">Download CV</span>
            </button>
          </div>
        </div>

        {/* Share-your-profile nudge — appears once the coach has filled in
            bio + qualifications AND has 5+ matches under their belt. Shows a
            live thumbnail of the actual OG share card if a public slug
            already exists, so the coach can see exactly what will appear
            when they paste the link into WhatsApp / X / LinkedIn. Dismissible
            via localStorage; the header Share button stays available. */}
        {!shareNudgeDismissed
          && (user?.personal_bio && user.personal_bio.trim())
          && (user?.coach_qualifications && user.coach_qualifications.trim())
          && (total_stats?.matches_played ?? 0) >= 5
          && (
          <Card
            className="border-emerald-200 overflow-hidden relative"
            style={{ background: 'linear-gradient(to right, #ecfdf5, #d1fae5)' }}
            data-testid="coach-office-share-nudge"
          >
            <button
              type="button"
              onClick={dismissShareNudge}
              className="absolute top-2 right-2 p-1.5 rounded-lg text-emerald-800/70 hover:text-emerald-900 hover:bg-white/60"
              aria-label="Dismiss share nudge"
              data-testid="coach-office-share-nudge-dismiss"
            >
              <X className="w-4 h-4" strokeWidth={2} />
            </button>
            <CardContent className="p-5">
              <div className="flex flex-col md:flex-row gap-5 items-start">
                {user?.public_slug ? (
                  <img
                    src={`${API}/coach/public/${user.public_slug}/og.png`}
                    alt="Your share card preview"
                    className="w-full md:w-72 rounded-xl border border-emerald-200 shadow-sm shrink-0"
                    style={{ aspectRatio: '1200 / 630' }}
                    loading="lazy"
                    data-testid="coach-office-share-nudge-preview"
                  />
                ) : (
                  <div
                    className="w-full md:w-72 rounded-xl border border-dashed border-emerald-300 bg-white/60 shrink-0 flex items-center justify-center text-emerald-800 text-sm text-center px-4"
                    style={{ aspectRatio: '1200 / 630' }}
                    data-testid="coach-office-share-nudge-preview-placeholder"
                  >
                    A branded share card will appear here once you enable your public profile.
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <span className="inline-flex items-center gap-1 text-xs uppercase tracking-wide font-bold text-emerald-700 mb-1">
                    <Sparkles className="w-3.5 h-3.5" strokeWidth={2} />
                    {referredCount >= 1 ? 'Your sharing is working' : 'Share your coaching profile'}
                  </span>
                  <h3 className="text-lg sm:text-xl font-bold text-emerald-950 font-title leading-snug">
                    {referredCount >= 1
                      ? `You've inspired ${referredCount} coach${referredCount === 1 ? '' : 'es'} to join Grassroots Coaching`
                      : 'You\u2019ve built a coaching CV worth showing off'}
                  </h3>
                  <p className="text-sm text-emerald-900/85 mt-1 leading-relaxed">
                    {referredCount >= 1
                      ? `Thanks for spreading the word. Every coach who signs up via your share link makes this platform stronger. Keep sharing — parents, clubs and other coaches are seeing your ${total_stats?.matches_played ?? 0} matches and ${total_stats?.win_percentage ?? 0}% win rate.`
                      : `${total_stats?.matches_played ?? 0} matches, ${total_stats?.win_percentage ?? 0}% win rate, bio and qualifications filled in — this is the kind of profile parents, clubs and other coaches actually want to see. Share it once and it works for you forever.`}
                  </p>
                  {referredCount >= 1 && (
                    <div className="mt-2" data-testid="coach-office-referred-count-badge">
                      <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-600 text-white text-xs font-bold px-3 py-1">
                        <Users className="w-3 h-3" strokeWidth={2.5} />
                        Coaches you&apos;ve inspired: {referredCount}
                      </span>
                    </div>
                  )}
                  <div className="flex flex-wrap gap-2 mt-3">
                    <button
                      type="button"
                      onClick={handleShare}
                      className="inline-flex items-center gap-1.5 rounded-xl px-4 py-2 text-sm font-semibold text-white bg-emerald-600 hover:bg-emerald-700"
                      data-testid="coach-office-share-nudge-share-btn"
                    >
                      <Share2 className="w-4 h-4" strokeWidth={2} />
                      Share now
                    </button>
                    <button
                      type="button"
                      onClick={dismissShareNudge}
                      className="inline-flex items-center gap-1.5 rounded-xl px-4 py-2 text-sm font-semibold text-emerald-800 hover:bg-white/60"
                      data-testid="coach-office-share-nudge-later-btn"
                    >
                      Not now
                    </button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Coach Profile Banner */}
        <Card
          className="text-white overflow-hidden border-transparent"
          style={{
            background: `linear-gradient(to right, ${user?.button_color || '#064E3B'}, color-mix(in srgb, ${user?.button_color || '#064E3B'} 75%, black))`,
          }}
          data-testid="coach-profile-banner"
        >
          <CardContent className="p-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                {(user?.profile_photo || user?.club_badge) ? (
                  <img
                    src={(() => {
                      const src = user.profile_photo || user.club_badge;
                      return src.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${src}` : src;
                    })()}
                    alt="Profile"
                    className="w-16 h-16 rounded-2xl object-cover"
                    data-testid="coach-profile-photo"
                  />
                ) : (
                  <div className="w-16 h-16 rounded-2xl bg-white/20 flex items-center justify-center">
                    <Crown className="w-8 h-8 text-white" strokeWidth={1.5} />
                  </div>
                )}
                <div className="leading-tight">
                  <h2 className="text-2xl font-bold">{coach_name}</h2>
                  {user?.current_role && (
                    <p className="text-white/85 text-sm -mt-0.5">{user.current_role}</p>
                  )}
                </div>
              </div>
              <div className="flex gap-6 sm:gap-8 text-center">
                <div>
                  <p className="text-3xl sm:text-4xl font-bold">{total_stats.total_seasons}</p>
                  <p className="text-white/70 text-sm">Seasons</p>
                </div>
                <div>
                  <p className="text-3xl sm:text-4xl font-bold">{total_stats.matches_played}</p>
                  <p className="text-white/70 text-sm">Matches</p>
                </div>
                <div>
                  <p className="text-3xl sm:text-4xl font-bold">{total_stats.win_percentage}%</p>
                  <p className="text-white/70 text-sm">Win Rate</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* About the Coach — bio + qualifications from Account Settings.
            Renders only when either field is populated so we don't show an
            empty card. Encourages new coaches to fill in their profile by
            appearing to reward setup with a proper profile panel. */}
        {(user?.personal_bio || user?.coach_qualifications) && (
          <Card data-testid="about-the-coach-card">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg font-semibold font-title flex items-center gap-2">
                <Users className="w-5 h-5 text-emerald-600" strokeWidth={1.5} />
                About the Coach
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {user?.personal_bio && (
                <p
                  className="text-sm sm:text-base text-gray-700 whitespace-pre-wrap leading-relaxed"
                  data-testid="about-the-coach-bio"
                >
                  {user.personal_bio}
                </p>
              )}
              {user?.coach_qualifications && (
                <div data-testid="about-the-coach-qualifications">
                  <div className="text-xs uppercase tracking-wide text-gray-500 font-semibold mb-2 flex items-center gap-1.5">
                    <Award className="w-3.5 h-3.5" strokeWidth={2} />
                    Qualifications
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {user.coach_qualifications
                      .split(/[,;\n]/)
                      .map((q) => q.trim())
                      .filter(Boolean)
                      .map((qual, i) => (
                        <Badge
                          key={i}
                          variant="outline"
                          className="rounded-full px-3 py-1 text-xs bg-emerald-50/60 border-emerald-200 text-emerald-800"
                          data-testid={`about-the-coach-qualification-${i}`}
                        >
                          {qual}
                        </Badge>
                      ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Career Overview Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
          <Card className="card-hover">
            <CardContent className="p-4 text-center">
              <TrendingUp className="w-8 h-8 mx-auto mb-2 text-emerald-500" strokeWidth={1.5} />
              <p className="text-3xl font-bold text-emerald-600">{total_stats.wins}</p>
              <p className="text-sm text-gray-500">Wins</p>
              <p className="text-xs text-emerald-600">{total_stats.win_percentage}%</p>
            </CardContent>
          </Card>

          <Card className="card-hover">
            <CardContent className="p-4 text-center">
              <Minus className="w-8 h-8 mx-auto mb-2 text-amber-500" strokeWidth={1.5} />
              <p className="text-3xl font-bold text-amber-600">{total_stats.draws}</p>
              <p className="text-sm text-gray-500">Draws</p>
              <p className="text-xs text-amber-600">{total_stats.draw_percentage}%</p>
            </CardContent>
          </Card>

          <Card className="card-hover">
            <CardContent className="p-4 text-center">
              <TrendingDown className="w-8 h-8 mx-auto mb-2 text-red-500" strokeWidth={1.5} />
              <p className="text-3xl font-bold text-red-600">{total_stats.losses}</p>
              <p className="text-sm text-gray-500">Losses</p>
              <p className="text-xs text-red-600">{total_stats.loss_percentage}%</p>
            </CardContent>
          </Card>

          <Card className="card-hover">
            <CardContent className="p-4 text-center">
              <Target className="w-8 h-8 mx-auto mb-2 text-blue-500" strokeWidth={1.5} />
              <p className="text-3xl font-bold text-gray-900">{total_stats.goals_scored}</p>
              <p className="text-sm text-gray-500">Goals Scored</p>
              <p className="text-xs text-blue-600">{total_stats.goals_per_game}/game</p>
            </CardContent>
          </Card>

          <Card className="card-hover">
            <CardContent className="p-4 text-center">
              <Shield className="w-8 h-8 mx-auto mb-2 text-purple-500" strokeWidth={1.5} />
              <p className="text-3xl font-bold text-gray-900">{total_stats.goals_conceded}</p>
              <p className="text-sm text-gray-500">Conceded</p>
              <p className="text-xs text-purple-600">{total_stats.conceded_per_game}/game</p>
            </CardContent>
          </Card>

          <Card className="card-hover">
            <CardContent className="p-4 text-center">
              <Award className="w-8 h-8 mx-auto mb-2 text-emerald-500" strokeWidth={1.5} />
              <p className="text-3xl font-bold text-gray-900">{total_stats.clean_sheets}</p>
              <p className="text-sm text-gray-500">Clean Sheets</p>
              <p className="text-xs text-emerald-600">{total_stats.matches_played > 0 ? Math.round((total_stats.clean_sheets / total_stats.matches_played) * 100) : 0}%</p>
            </CardContent>
          </Card>
        </div>

        {/* Parent engagement: shares this week */}
        <Card
          className="border-orange-200 bg-gradient-to-br from-orange-50 to-amber-50"
          data-testid="shares-this-week-card"
        >
          <CardContent className="p-5">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-orange-500 flex items-center justify-center shrink-0">
                <Share2 className="w-6 h-6 text-white" strokeWidth={2} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-orange-700">Parent Engagement</p>
                <p className="text-2xl sm:text-3xl font-bold text-gray-900 leading-tight">
                  <span data-testid="shares-week-count">{shareSummary.week}</span>
                  <span className="text-base font-medium text-gray-500 ml-2">
                    {shareSummary.week === 1 ? 'share' : 'shares'} this week
                  </span>
                </p>
                <p className="text-xs text-gray-500 mt-0.5">
                  {shareSummary.total > 0
                    ? `${shareSummary.total} all-time — every Form of the Week and Player Highlight you've shared with parents.`
                    : 'Hit "Share to Parents" on a player highlight or the Form of the Week widget to start tallying.'}
                </p>
              </div>
              {shareSummary.total > 0 && (
                <Sparkline values={shareSummary.daily} />
              )}
              {shareSummary.week > 0 && (
                <div className="hidden sm:flex items-center gap-1 px-3 py-1.5 rounded-full bg-orange-100 text-orange-700 text-xs font-semibold shrink-0">
                  <Flame className="w-3.5 h-3.5" strokeWidth={2.5} />
                  Active
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Records & Streaks */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Win Streak */}
          <Card className="bg-gradient-to-br from-amber-50 to-orange-50 border-amber-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-amber-500 flex items-center justify-center">
                  <Flame className="w-6 h-6 text-white" strokeWidth={1.5} />
                </div>
                <div>
                  <p className="text-sm text-amber-700">Longest Win Streak</p>
                  <p className="text-2xl font-bold text-amber-900">{total_stats.longest_win_streak} games</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Unbeaten Streak */}
          <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-blue-500 flex items-center justify-center">
                  <Zap className="w-6 h-6 text-white" strokeWidth={1.5} />
                </div>
                <div>
                  <p className="text-sm text-blue-700">Longest Unbeaten</p>
                  <p className="text-2xl font-bold text-blue-900">{total_stats.longest_unbeaten_streak} games</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Biggest Win */}
          <Card className="bg-gradient-to-br from-emerald-50 to-green-50 border-emerald-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-emerald-500 flex items-center justify-center">
                  <Star className="w-6 h-6 text-white" strokeWidth={1.5} />
                </div>
                <div>
                  <p className="text-sm text-emerald-700">Biggest Win</p>
                  {total_stats.biggest_win ? (
                    <>
                      <p className="text-xl font-bold text-emerald-900">{total_stats.biggest_win.score}</p>
                      <p className="text-xs text-emerald-600">vs {total_stats.biggest_win.opponent}</p>
                    </>
                  ) : (
                    <p className="text-lg text-emerald-600">No wins yet</p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Total Points */}
          <Card className="bg-gradient-to-br from-purple-50 to-pink-50 border-purple-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-purple-500 flex items-center justify-center">
                  <Medal className="w-6 h-6 text-white" strokeWidth={1.5} />
                </div>
                <div>
                  <p className="text-sm text-purple-700">Total Points</p>
                  <p className="text-2xl font-bold text-purple-900">{total_stats.points}</p>
                  <p className="text-xs text-purple-600">Goal Diff: {total_stats.goal_difference >= 0 ? '+' : ''}{total_stats.goal_difference}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Competition Breakdown */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Trophy className="w-5 h-5 text-emerald-600" strokeWidth={1.5} />
                League Record
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Total Matches</span>
                  <span className="font-bold">{total_stats.league_wins + total_stats.league_draws + total_stats.league_losses}</span>
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-emerald-600">Wins</span>
                    <span className="font-bold text-emerald-600">{total_stats.league_wins}</span>
                  </div>
                  <Progress value={(total_stats.league_wins / (total_stats.league_wins + total_stats.league_draws + total_stats.league_losses || 1)) * 100} className="h-2 bg-gray-200" />
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-amber-600">Draws: {total_stats.league_draws}</span>
                  <span className="text-red-600">Losses: {total_stats.league_losses}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Award className="w-5 h-5 text-amber-600" strokeWidth={1.5} />
                Cup Record
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Total Matches</span>
                  <span className="font-bold">{total_stats.cup_wins + total_stats.cup_draws + total_stats.cup_losses}</span>
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-emerald-600">Wins</span>
                    <span className="font-bold text-emerald-600">{total_stats.cup_wins}</span>
                  </div>
                  <Progress value={(total_stats.cup_wins / (total_stats.cup_wins + total_stats.cup_draws + total_stats.cup_losses || 1)) * 100} className="h-2 bg-gray-200" />
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-amber-600">Draws: {total_stats.cup_draws}</span>
                  <span className="text-red-600">Losses: {total_stats.cup_losses}</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Season by Season Breakdown */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-emerald-600" strokeWidth={1.5} />
              Season by Season Record
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {season_stats.map((season, index) => (
                <div 
                  key={season.id}
                  className={`p-4 rounded-xl border transition-all hover:shadow-md ${
                    season.is_current ? 'border-emerald-300 bg-emerald-50' : 'border-gray-200 bg-gray-50'
                  }`}
                >
                  <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                    {/* Season Info */}
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center font-bold ${
                        season.is_current ? 'bg-emerald-500 text-white' : 'bg-gray-300 text-gray-600'
                      }`}>
                        {index + 1}
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="font-bold text-gray-900">{season.name}</p>
                          {season.is_current && (
                            <Badge className="bg-emerald-500 text-white text-xs">Current</Badge>
                          )}
                        </div>
                        <p className="text-xs text-gray-500">
                          {formatDate(season.start_date)} - {season.end_date ? formatDate(season.end_date) : 'Present'}
                        </p>
                      </div>
                    </div>

                    {/* Stats */}
                    <div className="flex flex-wrap items-center gap-4 lg:gap-6">
                      <div className="text-center">
                        <p className="text-lg font-bold text-gray-900">{season.matches_played}</p>
                        <p className="text-xs text-gray-500">Played</p>
                      </div>
                      <div className="flex items-center gap-1">
                        <div className="text-center px-2">
                          <p className="text-lg font-bold text-emerald-600">{season.wins}</p>
                          <p className="text-xs text-gray-500">W</p>
                        </div>
                        <div className="text-center px-2">
                          <p className="text-lg font-bold text-amber-600">{season.draws}</p>
                          <p className="text-xs text-gray-500">D</p>
                        </div>
                        <div className="text-center px-2">
                          <p className="text-lg font-bold text-red-600">{season.losses}</p>
                          <p className="text-xs text-gray-500">L</p>
                        </div>
                      </div>
                      <div className="text-center">
                        <p className="text-lg font-bold text-gray-900">{season.goals_scored}-{season.goals_conceded}</p>
                        <p className="text-xs text-gray-500">Goals</p>
                      </div>
                      <div className="text-center min-w-[60px]">
                        <p className={`text-lg font-bold ${season.goal_difference >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                          {season.goal_difference >= 0 ? '+' : ''}{season.goal_difference}
                        </p>
                        <p className="text-xs text-gray-500">GD</p>
                      </div>
                      <div className="text-center min-w-[60px]">
                        <p className={`text-lg font-bold ${
                          season.win_percentage >= 50 ? 'text-emerald-600' : 
                          season.win_percentage >= 30 ? 'text-amber-600' : 'text-red-600'
                        }`}>
                          {season.win_percentage}%
                        </p>
                        <p className="text-xs text-gray-500">Win Rate</p>
                      </div>
                    </div>
                  </div>

                  {/* Win percentage bar */}
                  <div className="mt-3 pt-3 border-t border-gray-200/50">
                    <div className="flex gap-1 h-2 rounded-full overflow-hidden">
                      <div 
                        className="bg-emerald-500 transition-all" 
                        style={{ width: `${season.matches_played > 0 ? (season.wins / season.matches_played) * 100 : 0}%` }}
                      />
                      <div 
                        className="bg-amber-500 transition-all" 
                        style={{ width: `${season.matches_played > 0 ? (season.draws / season.matches_played) * 100 : 0}%` }}
                      />
                      <div 
                        className="bg-red-500 transition-all" 
                        style={{ width: `${season.matches_played > 0 ? (season.losses / season.matches_played) * 100 : 0}%` }}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}

